import type {
  DreGroup,
  FinancialAccount,
  FinancialCategory,
  FinancialEntry,
  FinancialTitle,
} from "./types";

// ── datas (sempre ISO yyyy-mm-dd no fuso local) ──────────────────────────────
export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export function parseIsoDate(iso: string): Date {
  return new Date(`${iso}T12:00:00`);
}
export function todayIso(): string {
  return toIsoDate(new Date());
}
export function addDays(iso: string, n: number): string {
  const d = parseIsoDate(iso);
  d.setDate(d.getDate() + n);
  return toIsoDate(d);
}
export function daysFrom(today: string, iso: string): number {
  return Math.round((parseIsoDate(iso).getTime() - parseIsoDate(today).getTime()) / 86_400_000);
}
export function monthBounds(year: number, month: number): { from: string; to: string } {
  return { from: toIsoDate(new Date(year, month, 1)), to: toIsoDate(new Date(year, month + 1, 0)) };
}
/** Data local de um timestamp (o dia em que o dinheiro passou). */
export function entryDate(entry: Pick<FinancialEntry, "occurred_at">): string {
  return toIsoDate(new Date(entry.occurred_at));
}

export const activeEntries = (entries: FinancialEntry[]) => entries.filter((e) => !e.voided_at);

// ── saldo ─────────────────────────────────────────────────────────────────────
export function accountBalances(accounts: FinancialAccount[], entries: FinancialEntry[], upTo?: string) {
  const byId = new Map<string, number>(accounts.map((a) => [a.id, Number(a.opening_balance) || 0]));
  for (const e of activeEntries(entries)) {
    if (upTo && entryDate(e) > upTo) continue;
    byId.set(e.account_id, (byId.get(e.account_id) ?? 0) + Number(e.amount));
  }
  return byId;
}

export function totalBalance(accounts: FinancialAccount[], entries: FinancialEntry[], upTo?: string): number {
  let total = 0;
  accountBalances(accounts, entries, upTo).forEach((v) => (total += v));
  return total;
}

// ── resumo mensal do razão (regime de caixa) ─────────────────────────────────
export interface MonthSummary {
  month: number;
  from: string;
  to: string;
  inflow: number;
  outflow: number;
  closing: number;
}

export function monthSummaries(
  accounts: FinancialAccount[],
  entries: FinancialEntry[],
  year: number,
  lastMonth: number,
): MonthSummary[] {
  const active = activeEntries(entries);
  const before = `${year}-01-01`;
  let balance = accounts.reduce((a, acc) => a + (Number(acc.opening_balance) || 0), 0);
  for (const e of active) if (entryDate(e) < before) balance += Number(e.amount);

  const out: MonthSummary[] = [];
  for (let m = 0; m <= lastMonth; m++) {
    const { from, to } = monthBounds(year, m);
    let inflow = 0;
    let outflow = 0;
    for (const e of active) {
      const d = entryDate(e);
      if (d < from || d > to) continue;
      const v = Number(e.amount);
      if (v > 0) inflow += v;
      else outflow += v;
    }
    balance += inflow + outflow;
    out.push({ month: m, from, to, inflow, outflow, closing: balance });
  }
  return out;
}

// ── DRE (regime de competência) ──────────────────────────────────────────────
export type MonthValues = (number | null)[];

export interface DreData {
  groups: Record<DreGroup, MonthValues>;
  categories: Map<string, MonthValues>;
  rol: MonthValues;
  ebitda: MonthValues;
  net: MonthValues;
  afterWithdrawals: MonthValues;
  activeMonths: number;
}

const emptyMonths = (active: number): MonthValues =>
  Array.from({ length: 12 }, (_, m) => (m < active ? 0 : null));

export function sumMonths(arrs: MonthValues[]): MonthValues {
  return Array.from({ length: 12 }, (_, m) => {
    let v: number | null = null;
    for (const a of arrs) if (a[m] !== null) v = (v ?? 0) + (a[m] as number);
    return v;
  });
}

export const yearTotal = (arr: MonthValues) => arr.reduce<number>((s, v) => s + (v ?? 0), 0);

/**
 * Soma os lançamentos do ano por mês de competência. Meses depois do atual ficam
 * nulos (ainda não aconteceram); o ano fechado tem os doze.
 */
export function buildDre(
  entries: FinancialEntry[],
  categories: FinancialCategory[],
  year: number,
  today: string,
): DreData {
  const currentYear = Number(today.slice(0, 4));
  const activeMonths = year < currentYear ? 12 : year > currentYear ? 0 : Number(today.slice(5, 7));
  const catById = new Map(categories.map((c) => [c.id, c]));
  const groups = Object.fromEntries(
    (["rob", "ded", "cat", "pes", "adm", "fin", "luc", "loan", "capex"] as DreGroup[]).map((g) => [g, emptyMonths(activeMonths)]),
  ) as Record<DreGroup, MonthValues>;
  const byCategory = new Map<string, MonthValues>();
  for (const c of categories) byCategory.set(c.id, emptyMonths(activeMonths));

  for (const e of activeEntries(entries)) {
    if (!e.category_id || Number(e.competence_date.slice(0, 4)) !== year) continue;
    const cat = catById.get(e.category_id);
    if (!cat) continue;
    const m = Number(e.competence_date.slice(5, 7)) - 1;
    if (m >= activeMonths) continue;
    const v = Number(e.amount);
    (groups[cat.dre_group][m] as number) += v;
    const arr = byCategory.get(cat.id)!;
    arr[m] = (arr[m] ?? 0) + v;
  }

  const rol = sumMonths([groups.rob, groups.ded]);
  const ebitda = sumMonths([rol, groups.cat, groups.pes, groups.adm]);
  const net = sumMonths([ebitda, groups.fin]);
  const afterWithdrawals = sumMonths([net, groups.luc]);
  return { groups, categories: byCategory, rol, ebitda, net, afterWithdrawals, activeMonths };
}

// ── títulos ───────────────────────────────────────────────────────────────────
export interface TitleBuckets {
  overdue: number;
  dueToday: number;
  next7: number;
}

export function titleBuckets(titles: FinancialTitle[], today: string): TitleBuckets {
  const b: TitleBuckets = { overdue: 0, dueToday: 0, next7: 0 };
  for (const t of titles) {
    if (t.status !== "open") continue;
    const d = daysFrom(today, t.due_date);
    const v = Number(t.amount);
    if (d < 0) b.overdue += v;
    else if (d === 0) b.dueToday += v;
    else if (d <= 7) b.next7 += v;
  }
  return b;
}

export interface DebtorSummary {
  name: string;
  detail: string | null;
  total: number;
  overdue: number;
  count: number;
  maxDaysLate: number;
  titles: FinancialTitle[];
}

export function debtors(titles: FinancialTitle[], today: string): DebtorSummary[] {
  const map = new Map<string, DebtorSummary>();
  for (const t of titles) {
    if (t.kind !== "receivable" || t.status !== "open") continue;
    const d = daysFrom(today, t.due_date);
    const cur =
      map.get(t.counterparty_name) ??
      { name: t.counterparty_name, detail: t.counterparty_detail, total: 0, overdue: 0, count: 0, maxDaysLate: 0, titles: [] };
    cur.total += Number(t.amount);
    cur.count += 1;
    cur.titles.push(t);
    if (d < 0) {
      cur.overdue += Number(t.amount);
      cur.maxDaysLate = Math.max(cur.maxDaysLate, -d);
    }
    map.set(t.counterparty_name, cur);
  }
  return [...map.values()].sort((a, b) => b.overdue - a.overdue || b.total - a.total);
}

/** Parse "1.234,56" ou "1234.56" em número. */
export function parseMoney(value: string): number {
  const s = value.trim();
  if (!s) return 0;
  const normalized = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}
